import { Col, Divider, Row } from "antd";
import groupBy from "lodash/groupBy";
import { useTranslation } from "react-i18next";
import eucsLogo from "../../../../assets/images/eucs.png";
import imagePlaceholderLogo from "../../../../assets/images/image-placeholder.svg";
import lunaLogo from "../../../../assets/images/luna.svg";
import monlyLogo from "../../../../assets/images/monly.svg";
import nsureLogo from "../../../../assets/images/nsure.png";
import vzdyViacLogo from "../../../../assets/images/vzdyviac.png";
import { rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { useHasPermissions } from "../../../auth/hooks";
import { InstitutionType } from "../../../institution/enums";
import { resolveInstitutionLogo } from "../../../institution/utils";
import { PartnerSsoSubType, PartnerSsoType } from "../../enums";
import { PartnerConfig } from "../../types";
import { PartnerConfigsCardView } from "./PartnerConfigsCardView";

const CALCULATOR_SSO_SUB_TYPES = [
  PartnerSsoSubType.FINGO_NON_LIFE_CALC,
  PartnerSsoSubType.FINGO_LIFE_CALC,
  PartnerSsoSubType.FINGO_LOAN_CALC,
  PartnerSsoSubType.FINGO_INVEST_CALC,
];

interface Props {
  configList: PartnerConfig[];
  onPartnerSsoGet: (ssoType: PartnerSsoType, username?: string) => void;
  onLogoutClick: (configs: PartnerConfig[]) => void;
  onUpdateClick: (configs: PartnerConfig[]) => void;
}

const groupConfigs = (
  configList: PartnerConfig[],
  groupKey: (config: PartnerConfig) => string | undefined,
): PartnerConfig[][] => {
  const groups = groupBy(configList, groupKey);
  return Object.keys(groups).map((groupKey) => groups[groupKey] as PartnerConfig[]);
};

const isCalculatorSubTypeConfig = (config: PartnerConfig) =>
  !!config.ssoSubType && CALCULATOR_SSO_SUB_TYPES.includes(config.ssoSubType);

const isHelperSubTypeConfig = (config: PartnerConfig) => config.ssoSubType === PartnerSsoSubType.FINGO_HELPER;

const resolveExternalPortalLogo = (ssoType?: PartnerSsoType) => {
  switch (ssoType) {
    case PartnerSsoType.MONLY:
      return monlyLogo;
    case PartnerSsoType.VZDY_VIAC:
      return vzdyViacLogo;
    case PartnerSsoType.EUCS:
      return eucsLogo;
    case PartnerSsoType.NSURE:
      return nsureLogo;
    case PartnerSsoType.LUNA:
      return lunaLogo;
    default:
      return imagePlaceholderLogo;
  }
};

export const PartnerConfigListView = ({ configList, onPartnerSsoGet, onLogoutClick, onUpdateClick }: Props) => {
  const { t } = useTranslation();

  const hasAdminPartnerConfigsPermission = useHasPermissions(Permission.ADMIN_PARTNER_CONFIGS);

  const extPortals = groupConfigs(
    configList.filter(
      (config) =>
        config.ssoType === PartnerSsoType.MONLY ||
        config.ssoType === PartnerSsoType.LUNA ||
        config.ssoType === PartnerSsoType.VZDY_VIAC ||
        config.ssoType === PartnerSsoType.EUCS ||
        config.ssoType === PartnerSsoType.NSURE,
    ),
    (config) => config.ssoType,
  );

  const insuranceCompanies = groupConfigs(
    configList.filter((config) => config.institution?.type === InstitutionType.INSURANCE_COMPANY),
    (config) => config.institution?.id,
  );

  const banks = groupConfigs(
    configList.filter((config) => config.institution?.type === InstitutionType.BANK),
    (config) => config.institution?.id,
  );

  const securitiesBrokers = groupConfigs(
    configList.filter((config) => config.institution?.type === InstitutionType.SECURITIES_BROKER),
    (config) => config.institution?.id,
  );

  const others = groupConfigs(
    configList.filter(
      (config) =>
        config.institution?.type !== InstitutionType.INSURANCE_COMPANY &&
        config.institution?.type !== InstitutionType.BANK &&
        config.institution?.type !== InstitutionType.SECURITIES_BROKER &&
        config.ssoType !== PartnerSsoType.MONLY &&
        config.ssoType !== PartnerSsoType.LUNA &&
        config.ssoType !== PartnerSsoType.VZDY_VIAC &&
        config.ssoType !== PartnerSsoType.EUCS &&
        config.ssoType !== PartnerSsoType.NSURE &&
        !isCalculatorSubTypeConfig(config) &&
        !isHelperSubTypeConfig(config),
    ),
    (config) => config.id,
  );

  const calculators = groupConfigs(configList.filter(isCalculatorSubTypeConfig), (config) => config.id);

  const helpers = groupConfigs(configList.filter(isHelperSubTypeConfig), (config) => config.id);

  return configList.length > 0 ? (
    <>
      {extPortals.length > 0 && (
        <Row gutter={rowGutter}>
          {extPortals
            .filter((configs) => configs[0]?.ssoType !== PartnerSsoType.LUNA || hasAdminPartnerConfigsPermission)
            .map((configs) => (
              <Col key={configs[0]?.id} sm={8} md={6}>
                <PartnerConfigsCardView
                  configs={configs}
                  hasAdminPartnerConfigsPermission={hasAdminPartnerConfigsPermission}
                  logoProvider={() => resolveExternalPortalLogo(configs[0]?.ssoType)}
                  onPartnerSsoGet={onPartnerSsoGet}
                  onLogoutClick={onLogoutClick}
                  onUpdateClick={onUpdateClick}
                />
              </Col>
            ))}
        </Row>
      )}

      {insuranceCompanies.length > 0 && (
        <>
          <Divider titlePlacement="start">
            <b>{t("partnerConfig.titles.insuranceCompanies")}</b>
          </Divider>
          <Row gutter={rowGutter}>
            {insuranceCompanies.map((configs) => (
              <Col key={configs[0]?.id} sm={8} md={6}>
                <PartnerConfigsCardView
                  configs={configs}
                  hasAdminPartnerConfigsPermission={hasAdminPartnerConfigsPermission}
                  logoProvider={() => (configs[0]?.institution ? resolveInstitutionLogo(configs[0].institution) : "")}
                  onPartnerSsoGet={onPartnerSsoGet}
                  onLogoutClick={onLogoutClick}
                  onUpdateClick={onUpdateClick}
                />
              </Col>
            ))}
          </Row>
        </>
      )}

      {banks.length > 0 && (
        <>
          <Divider titlePlacement="start">
            <b>{t("partnerConfig.titles.banks")}</b>
          </Divider>
          <Row gutter={rowGutter}>
            {banks.map((configs) => (
              <Col key={configs[0]?.id} sm={8} md={6}>
                <PartnerConfigsCardView
                  configs={configs}
                  hasAdminPartnerConfigsPermission={hasAdminPartnerConfigsPermission}
                  logoProvider={() => (configs[0]?.institution ? resolveInstitutionLogo(configs[0].institution) : "")}
                  onPartnerSsoGet={onPartnerSsoGet}
                  onLogoutClick={onLogoutClick}
                  onUpdateClick={onUpdateClick}
                />
              </Col>
            ))}
          </Row>
        </>
      )}

      {securitiesBrokers.length > 0 && (
        <>
          <Divider titlePlacement="start">
            <b>{t("partnerConfig.titles.securitiesBrokers")}</b>
          </Divider>
          <Row gutter={rowGutter}>
            {securitiesBrokers.map((configs) => (
              <Col key={configs[0]?.id} sm={8} md={6}>
                <PartnerConfigsCardView
                  configs={configs}
                  hasAdminPartnerConfigsPermission={hasAdminPartnerConfigsPermission}
                  logoProvider={() => (configs[0]?.institution ? resolveInstitutionLogo(configs[0].institution) : "")}
                  onPartnerSsoGet={onPartnerSsoGet}
                  onLogoutClick={onLogoutClick}
                  onUpdateClick={onUpdateClick}
                />
              </Col>
            ))}
          </Row>
        </>
      )}

      {others.length > 0 && (
        <>
          <Divider titlePlacement="start">
            <b>{t("partnerConfig.titles.other")}</b>
          </Divider>
          <Row gutter={rowGutter}>
            {others.map((configs) => (
              <Col key={configs[0]?.id} sm={8} md={6}>
                <PartnerConfigsCardView
                  configs={configs}
                  hasAdminPartnerConfigsPermission={hasAdminPartnerConfigsPermission}
                  logoProvider={() =>
                    configs[0]?.institution ? resolveInstitutionLogo(configs[0]?.institution) : imagePlaceholderLogo
                  }
                  onPartnerSsoGet={onPartnerSsoGet}
                  onLogoutClick={onLogoutClick}
                  onUpdateClick={onUpdateClick}
                />
              </Col>
            ))}
          </Row>
        </>
      )}

      {calculators.length > 0 && (
        <>
          <Divider titlePlacement="start">
            <b>{t("partnerConfig.titles.calculators")}</b>
          </Divider>
          <Row gutter={rowGutter}>
            {calculators.map((configs) => (
              <Col key={configs[0]?.id} sm={8} md={6}>
                <PartnerConfigsCardView
                  configs={configs}
                  hasAdminPartnerConfigsPermission={hasAdminPartnerConfigsPermission}
                  logoProvider={() =>
                    configs[0]?.institution ? resolveInstitutionLogo(configs[0]?.institution) : imagePlaceholderLogo
                  }
                  onPartnerSsoGet={onPartnerSsoGet}
                  onLogoutClick={onLogoutClick}
                  onUpdateClick={onUpdateClick}
                />
              </Col>
            ))}
          </Row>
        </>
      )}

      {helpers.length > 0 && (
        <>
          <Divider titlePlacement="start">
            <b>{t("partnerConfig.titles.helpers")}</b>
          </Divider>
          <Row gutter={rowGutter}>
            {helpers.map((configs) => (
              <Col key={configs[0]?.id} sm={8} md={6}>
                <PartnerConfigsCardView
                  configs={configs}
                  hasAdminPartnerConfigsPermission={hasAdminPartnerConfigsPermission}
                  logoProvider={() =>
                    configs[0]?.institution ? resolveInstitutionLogo(configs[0]?.institution) : imagePlaceholderLogo
                  }
                  onPartnerSsoGet={onPartnerSsoGet}
                  onLogoutClick={onLogoutClick}
                  onUpdateClick={onUpdateClick}
                />
              </Col>
            ))}
          </Row>
        </>
      )}
    </>
  ) : (
    <div className="sub-header-info dashed center-align">{t("partnerConfig.helpers.noData")}</div>
  );
};
